import React from "react";
import { ShimmerEffect } from "@/components/shimmer";
import { assetUrl, cn } from "utils";
import { useSidebar } from "ui/components/sidebar";

/**
 * A collapsible sidebar header component that adapts to the sidebar's collapse state.
 * Shows full header (large image, title, and subtitle) when expanded, and only a small image/fallback when collapsed.
 *
 * @param {Object} props - Component props
 * @param {string} props.title - The primary header text (e.g. project name or employee full name)
 * @param {React.ReactNode} [props.subtitle] - Optional sub-text or badge element (e.g. status badge or job title)
 * @param {string} [props.logo] - Optional asset ID or URL. If provided, renders an image via assetUrl or directly if it's already a full URL.
 * @param {string} [props.fallbackText] - Optional text for fallback initials/character
 * @param {boolean} [props.isLoading=false] - Whether to show the loading shimmer
 * @param {string} [props.className=""] - CSS classes for the container
 * @param {string} [props.imageClassName=""] - CSS classes for the image element (e.g. rounded-full or rounded)
 */
export function CollapsibleSidebarHeader({
  title,
  subtitle,
  logo,
  fallbackText,
  isLoading = false,
  className = "",
}) {
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";

  if (isLoading) {
    return (
      <div className={cn("flex flex-col gap-2 p-4", className)}>
        <div className={cn("aspect-square transition-all duration-200", isCollapsed ? "size-6" : "size-44")}>
          <ShimmerEffect className="w-full h-full rounded" />
        </div>
        {!isCollapsed && (
          <div>
            <ShimmerEffect className="w-40 h-5" />
            <ShimmerEffect className="w-20 h-4 mt-1" />
          </div>
        )}
      </div>
    );
  }

  if (!title) {
    return null;
  }

  const fallback = fallbackText || title[0] || "?";

  // Check if logo is a full URL or just a Directus asset ID
  const logoSrc = logo ? (logo.startsWith("http://") || logo.startsWith("https://") || logo.startsWith("/") ? logo : assetUrl(logo)) : null;

  const imageContent = logoSrc ? (
    <img
      src={logoSrc}
      alt={`${title} logo`}
      className={cn("w-full h-full object-cover rounded")}
    />
  ) : (
    <div className={cn("w-full h-full bg-gray-200 flex items-center justify-center rounded")}>
      <span className={cn(
        "text-gray-500 font-semibold transition-all duration-200",
        isCollapsed ? "text-xs" : "text-6xl"
      )}>
        {fallback}
      </span>
    </div>
  );

  return (
    <div className={cn("flex flex-col gap-2 p-4", className)}>
      {/* Logo container - sizes dynamically transition */}
      <div className={cn(
        "aspect-square transition-all duration-200 flex items-center justify-center overflow-hidden",
        isCollapsed ? "size-6" : "size-44"
      )}>
        {imageContent}
      </div>

      {/* Details container - hidden when collapsed */}
      {!isCollapsed && (
        <div className="transition-all duration-200">
          <h2 className="font-semibold text-lg leading-tight truncate" title={title}>
            {title}
          </h2>
          {subtitle && <div className="mt-1 flex items-center gap-1">{subtitle}</div>}
        </div>
      )}
    </div>
  );
}
